<template>
  <div id="app" data-v-app="">
    <div class="d-flex justify-content-center spinner-border text-primary spinner-position form" v-if="loading"></div>
    <div class="spinner" v-if='loading'></div>
    <div class="row-wrapper">
      <div class="col-8">
        <div class="image"></div>
        <div class="row-wrapper" id="picture" :style="backgroundStyle"></div>
      </div>
      <div class="col-4">
        <div id="login-card" class="card-body">
          <div class="logo-brand">
            <img id="logo-brand-omnih" :src="corporateLogo" alt="logo">
            <hr class="separator">
          </div>

          <!-- Success State -->
          <div v-if="status" class="success-state">
            <div class="success-icon-wrapper">
              <i class="fas fa-envelope-open-text success-icon"></i>
            </div>
            <h4 class="success-title">We sent an email</h4>
            <p class="success-message">
              Please check your email and follow the instructions provided to reset your password. 
              If you do not see the email in your inbox, please check your spam or junk folder.
            </p>
            <router-link :to="{ name: 'ExtranetLogin' }" class="back-to-login-link">
              <i class="fas fa-arrow-left"></i>
              Back to Login
            </router-link>
          </div>

          <!-- Form State -->
          <div v-else class="forgot-form-wrapper">
            <h4 class="form-title">Forgot your password?</h4>
            <p class="form-subtitle">
              Please enter the email you use to sign in to the Extranet system.
            </p>

            <!-- Error Message -->
            <div v-if="validation" class="custom-alert">
              <div class="alert-content">
                <span class="alert-icon">&#9888;</span>
                <p>{{ validation }}</p>
              </div>
            </div>

            <o-form id="form-forgot" class="form-forgot">
              <o-form-group title="Email" name="email">
                <o-input-text 
                  name="email" 
                  v-model="email" 
                  type="email" 
                  :required="true" 
                  placeholder="email"
                  @input="clearValidation"
                />
              </o-form-group>

              <div class="d-flex justify-content-center mt-4">
                <o-button class="reset-button" @click="forgotPass">
                  Request Password Reset
                </o-button>
              </div>

              <div class="d-flex justify-content-center mt-3">
                <router-link :to="{ name: 'ExtranetLogin' }" class="back-to-login-link">
                  <i class="fas fa-arrow-left"></i>
                  Back to Login
                </router-link>
              </div>
            </o-form>
          </div>

          <div class="simple-footer">
            <small>
              Copyright
              <span>©</span>
              PT. Omni Hotelier Internasional
              <span>{{ currentYear }}</span>
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { OForm, OFormGroup, OInputText, OButton } from '@/components/OmnihControl'

export default {
  name: 'ExtranetForgotPassword',
  components: {
    OForm,
    OFormGroup,
    OInputText,
    OButton,
  },
  setup() {
    const store = useStore()

    const email = ref('')
    const validation = ref('')
    const status = ref(false)
    const loading = ref(false)
    const currentYear = new Date().getFullYear()
    const corporateLogo = ref('/img/logos/extranet-logo.avif')
    const loginBackground = ref('/img/temporary-login.png')

    const backgroundStyle = computed(() => ({
      backgroundImage: `url(${loginBackground.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }))

    const forgotPass = () => {
      if (!email.value) {
        validation.value = 'Please enter a valid email address.'
        return
      }

      loading.value = true

      store.dispatch('Auth/sendExtranetResetRequest', email.value)
        .then(result => {
          if (result.status) {
            status.value = true
          }
        }).catch(response => {
          const error_msg = response?.data?.error || response?.data?.message || 'Failed to send reset email. Please try again.'
          validation.value = error_msg
        }).finally(() => {
          loading.value = false
        })
    }

    const clearValidation = () => {
      validation.value = ''
    }

    return {
      email,
      validation,
      status,
      loading,
      currentYear,
      corporateLogo,
      loginBackground,
      backgroundStyle,
      forgotPass,
      clearValidation,
    }
  }
}
</script>

<style scoped>
.form-forgot {
  margin-top: 1%;
}
.row-wrapper {
  display: flex;
  width: 100%;
  height: 100vh;
}
#picture {
  align-items: center;
}
.col-8 {
  width: 66.66%;
  display: grid;
  align-items: center;
  position: relative;
  overflow: hidden;
}
.image {
  height: 50%;
  object-fit: contain;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: absolute;
  top: 25%;
  left: 0;
  right: 0;
  bottom: 0;
}
.col-4 {
  width: 33.33%;
  display: flex;
  justify-content: center;
  align-items: start;
  position: relative;
  background-color: white;
  height: 100vh;
  box-shadow: -10px 0 20px rgba(0, 0, 0, 0.1);
}
.card-body#login-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: small;
  width: 100%;
  max-width: 400px;
  padding: 2rem;
  overflow: auto;
}
.logo-brand {
  justify-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
#logo-brand-omnih {
  display: block;
  width: 85%;
  margin-bottom: 1rem;
}
.separator {
  border: none;
  height: 1px;
  background-color: #e2e8f0;
  width: 100%;
  margin: 1.5rem 0;
}
.simple-footer {
  margin-top: 30dvh;
  text-align: center;
  color: #98a6ad;
  display: flex;
  justify-content: center;
}
small {
  display: inline-block;
  width: 100%;
  text-align: center;
}

/* Form Styles */
.forgot-form-wrapper {
  text-align: center;
}
.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}
.form-subtitle {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 1rem 0;
}
.success-icon-wrapper {
  margin-bottom: 1rem;
}
.success-icon {
  font-size: 3rem;
  color: #48bb78;
}
.success-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.75rem;
}
.success-message {
  font-size: 0.875rem;
  color: #718096;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

/* Button */
.reset-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  width: 100%;
  font-size: 14px;
  color: white;
  background: linear-gradient(135deg, #ff416c, #ff4b2b);
  border-radius: 8px;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(255, 65, 108, 0.3);
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}
.reset-button:hover {
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  transform: scale(1.02);
  color: white;
}

/* Back Link */
.back-to-login-link {
  color: #ff416c;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.back-to-login-link:hover {
  color: #ee0979;
  text-decoration: none;
}

/* Alert */
.custom-alert {
  position: relative;
  background: linear-gradient(135deg, #ff4b2b, #ff416c);
  color: white;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(255, 65, 108, 0.3);
  animation: fadeInDown 0.6s ease-out;
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.alert-content {
  display: flex;
  align-items: center;
  width: 100%;
}
.alert-icon {
  font-size: 18px;
  margin-right: 12px;
}
.custom-alert p {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Spinner */
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 65, 108, 0.1);
  border-top-color: #ff416c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}
@keyframes spin {
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@media (max-width: 768px) {
  .col-8 {
    display: none;
  }
  .col-4 {
    width: 100%;
    max-width: 100%;
  }
  .row-wrapper {
    justify-content: center;
  }
  .card-body#login-card {
    margin: 10%;
  }
}
</style>
